import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

// @mui material components
import { useTheme } from "@mui/material/styles";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Divider from "@mui/material/Divider";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import DauTrang, { NhanDau } from "components/DauTrang";
import TheChiSo from "components/TheChiSo";
import { giaoDichApi } from "api/giaoDich";
import { useAuth } from "context/AuthContext";
import useTour from "hooks/useTour";
import buocTrangChu from "tours/trangChu";

const VAI_TRO_THU_MUA = ["nhan_vien", "admin"];

function TrangChu() {
  const navigate = useNavigate();
  const { palette } = useTheme();
  const { vaiTro } = useAuth();
  const [giaoDichHomNay, setGiaoDichHomNay] = useState([]);
  const [dangTai, setDangTai] = useState(true);

  // Nhân viên chỉ thấy phiếu MÌNH ghi (đúng nghĩa "việc của tôi hôm nay").
  // Mọi vai trò còn lại (admin, kế toán, kho, ban giám đốc) không có lý do gì
  // bị giới hạn xem đúng phần mình tự tay ghi — họ cần thấy TOÀN BỘ giao dịch
  // trong ngày để nắm được tình hình chung, không phải "của riêng họ".
  const xemToanBo = !!vaiTro && vaiTro !== "nhan_vien";

  useEffect(() => {
    if (!vaiTro) return;
    const homNay = new Date().toISOString().slice(0, 10);
    setDangTai(true);
    const huy = xemToanBo
      ? giaoDichApi.langNgheTrongKhoang(
          homNay,
          homNay,
          (ds) => {
            setGiaoDichHomNay(ds);
            setDangTai(false);
          },
          () => setDangTai(false)
        )
      : giaoDichApi.langNgheCuaToi(
          homNay,
          (ds) => {
            setGiaoDichHomNay(ds);
            setDangTai(false);
          },
          () => setDangTai(false)
        );
    return huy;
  }, [vaiTro, xemToanBo]);

  // 1 dòng/PO (hoá đơn) — 1 lượt thu mua nhiều loại bơ vẫn là 1 hoá đơn, kể
  // cả ở "Giao dịch hôm nay". Tổng kg/tiền cộng dồn từ items[] bên trong.
  const dsPO = giaoDichHomNay.map((po) => ({
    ...po,
    tongKgPO: po.items.reduce((s, it) => s + it.khoiLuong, 0),
  }));
  const tongKhoiLuong = dsPO.reduce((s, po) => s + po.tongKgPO, 0);
  const tongTien = dsPO.reduce((s, po) => s + po.items.reduce((s2, it) => s2 + it.thanhTien, 0), 0);

  const duocThuMua = VAI_TRO_THU_MUA.includes(vaiTro);
  const ngayHienThi = new Date().toLocaleDateString("vi-VN");

  const buocTour = useMemo(() => buocTrangChu({ duocThuMua }), [duocThuMua]);
  useTour("trang-chu", buocTour);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={{ xs: 1.5, md: 3 }}>
        <DauTrang
          nhan="Bảng điều khiển"
          tieuDe="Thu mua hôm nay"
          moTa={
            xemToanBo
              ? `Toàn bộ giao dịch trong ngày ${ngayHienThi}.`
              : `Các giao dịch bạn đã ghi trong ngày ${ngayHienThi}.`
          }
        >
          {duocThuMua && (
            <MDButton
              id="tour-trang-chu-thu-mua-btn"
              variant="gradient"
              color="info"
              onClick={() => navigate("/thu-mua/chu-vuon")}
            >
              <Icon sx={{ mr: 0.75 }}>shopping_basket</Icon>
              Thu mua
            </MDButton>
          )}
        </DauTrang>

        {/* Ba sắc của "trái bơ bổ đôi" (vỏ / lá / hạt) làm mã màu cho 3 chỉ số
            — đọc qua theme thay vì viết mã hex, xem assets/theme/base/colors.js. */}
        <Grid container spacing={2.5}>
          <Grid item xs={6} sm={4}>
            <TheChiSo
              nhan="Giao dịch"
              giaTri={giaoDichHomNay.length}
              donVi="phiếu thu mua"
              icon="receipt_long"
              mau={palette.primary.main}
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TheChiSo
              nhan="Khối lượng"
              giaTri={`${tongKhoiLuong.toLocaleString("vi-VN")} kg`}
              donVi="đã cân trong ngày"
              icon="scale"
              mau={palette.info.main}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TheChiSo
              nhan="Thành tiền"
              giaTri={tongTien.toLocaleString("vi-VN")}
              donVi="đồng"
              icon="payments"
              mau={palette.gradients.avocado.main}
            />
          </Grid>
        </Grid>

        <MDBox mt={4} id="tour-trang-chu-giao-dich-gan-day">
          <Card>
            <MDBox px={3} pt={2.5} pb={1.5}>
              <NhanDau>Hôm nay</NhanDau>
              <MDTypography variant="h6">Giao dịch gần đây</MDTypography>
            </MDBox>
            <Divider sx={{ my: 0 }} />
            {dangTai ? (
              <MDBox px={3} py={3}>
                <MDTypography variant="button" color="text">
                  Đang tải...
                </MDTypography>
              </MDBox>
            ) : dsPO.length === 0 ? (
              <MDBox px={3} py={4} textAlign="center">
                <Icon sx={({ palette }) => ({ color: palette.grey[400], fontSize: "2.5rem" })}>
                  receipt_long
                </Icon>
                <MDTypography variant="button" fontWeight="medium" display="block" mt={1}>
                  Chưa có giao dịch nào hôm nay.
                </MDTypography>
                {duocThuMua && (
                  <MDTypography variant="caption" color="text" display="block" mt={0.5}>
                    Bấm “Thu mua” ở trên để ghi phiếu đầu tiên.
                  </MDTypography>
                )}
              </MDBox>
            ) : (
              <List sx={{ py: 0 }}>
                {dsPO.map((po, i) => (
                  <MDBox key={po.id}>
                    <ListItem
                      sx={({ palette }) => ({
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        px: 3,
                        py: 1.75,
                        transition: "background-color 160ms ease",
                        "&:hover": { backgroundColor: palette.grey[100] },
                      })}
                    >
                      <MDBox minWidth={0}>
                        <MDTypography variant="button" fontWeight="medium" display="block">
                          {po.tenChuVuon}
                        </MDTypography>
                        <MDTypography variant="caption" color="text" display="block">
                          {po.tenVuon}
                          {xemToanBo ? ` · ${po.tenNhanVien ?? "—"}` : ""}
                        </MDTypography>
                      </MDBox>
                      <MDBox textAlign="right" flexShrink={0}>
                        <MDTypography variant="button" fontWeight="bold" display="block">
                          {po.tongKgPO.toLocaleString("vi-VN")}
                        </MDTypography>
                        <MDTypography variant="caption" color="text" display="block">
                          kg
                        </MDTypography>
                      </MDBox>
                    </ListItem>
                    {i < dsPO.length - 1 && <Divider sx={{ my: 0 }} />}
                  </MDBox>
                ))}
              </List>
            )}
          </Card>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default TrangChu;
